import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { createContext } from 'react';
import { MessageCircle } from 'lucide-react';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation } from 'react-router-dom';
import { useApiClient } from '../../lib/apiClient';
import { useAppointmentModalStore } from '../../stores/appointmentModalStore';
import SentinelDrawer from './SentinelDrawer';
import {
  SentinelActionProposal,
  SentinelContextEnvelope,
  SentinelConversation,
  SentinelMessage,
  SentinelScopeType,
  SentinelSelectedEntities,
} from './types';

type SentinelContextValue = {
  isOpen: boolean;
  openSentinel: () => void;
  closeSentinel: () => void;
  toggleSentinel: () => void;
};

const SentinelContext = createContext<SentinelContextValue | null>(null);

function mapModule(pathname: string) {
  if (pathname.startsWith('/patients')) return 'Patients';
  if (pathname.startsWith('/appointments')) return 'Calendar';
  if (pathname.startsWith('/billing')) return 'Billing';
  if (pathname.startsWith('/reports')) return 'Reports';
  if (pathname.startsWith('/settings')) return 'Admin';
  if (pathname.startsWith('/dashboard') || pathname.startsWith('/command-center')) return 'Dashboard';
  if (pathname.startsWith('/telemedicine')) return 'Telemedicine';
  return 'General';
}

function isInAppRoute(pathname: string) {
  return (
    pathname.startsWith('/dashboard') ||
    pathname.startsWith('/command-center') ||
    pathname.startsWith('/patients') ||
    pathname.startsWith('/appointments') ||
    pathname.startsWith('/telemedicine') ||
    pathname.startsWith('/billing') ||
    pathname.startsWith('/reports') ||
    pathname.startsWith('/core-clinical') ||
    pathname.startsWith('/room-management') ||
    pathname.startsWith('/settings') ||
    pathname.startsWith('/revenue-insights') ||
    pathname.startsWith('/genomic-data-integration') ||
    pathname.startsWith('/adaptive-ai-systems')
  );
}

function computeScope(selected: SentinelSelectedEntities, module: string): SentinelScopeType {
  if (selected.task_id) return 'task';
  if (selected.appointment_id) return 'appointment';
  if (selected.patient_id) return 'patient';
  if (module === 'Admin') return 'admin';
  return 'global';
}

function computeScopeId(selected: SentinelSelectedEntities, scope: SentinelScopeType) {
  if (scope === 'task') return selected.task_id || undefined;
  if (scope === 'appointment') return selected.appointment_id || undefined;
  if (scope === 'patient') return selected.patient_id || undefined;
  return undefined;
}

export function SentinelProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const { isAuthenticated } = useAuth0();
  const { fetchApi, fetchJson } = useApiClient();
  const modalPending = useAppointmentModalStore((state) => state.pendingData);
  const modalOpen = useAppointmentModalStore((state) => state.isOpen);
  const sentinelEnabled = isAuthenticated && isInAppRoute(location.pathname);

  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [conversations, setConversations] = useState<SentinelConversation[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<SentinelMessage[]>([]);
  const [proposals, setProposals] = useState<SentinelActionProposal[]>([]);
  const [currentEnvelope, setCurrentEnvelope] = useState<SentinelContextEnvelope | null>(null);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);

  const currentContextHint = useMemo(() => {
    const selected: SentinelSelectedEntities = {};
    const path = location.pathname;
    const params = new URLSearchParams(location.search);
    const patientMatch = path.match(/^\/patients\/([^/]+)$/);
    if (patientMatch?.[1]) {
      selected.patient_id = patientMatch[1];
      const encounterId = params.get('encounterId');
      if (encounterId) selected.encounter_id = encounterId;
    }
    if (path.startsWith('/appointments')) {
      const openId = params.get('openId');
      if (openId) selected.appointment_id = openId;
    }
    if (modalOpen && modalPending) {
      if (typeof modalPending.id === 'string' && modalPending.id) selected.appointment_id = modalPending.id;
      if (typeof modalPending.patientId === 'string' && modalPending.patientId) selected.patient_id = modalPending.patientId;
      if (typeof modalPending.providerId === 'string' && modalPending.providerId) selected.provider_id = modalPending.providerId;
    }
    const taskId = params.get('taskId');
    if (taskId) selected.task_id = taskId;
    const providerId = params.get('providerId');
    if (providerId && !selected.provider_id) selected.provider_id = providerId;

    return {
      route: `${location.pathname}${location.search}`,
      module: mapModule(location.pathname),
      selected_entities: selected,
      tenant_id: tenantId,
      user_role: userRole,
    };
  }, [location.pathname, location.search, modalOpen, modalPending, tenantId, userRole]);

  const loadCurrentUser = useCallback(async () => {
    try {
      const me = await fetchJson<{
        id: string;
        role: string;
        organization: { id: string };
      }>('/users/me');
      setTenantId(me.organization?.id ?? null);
      setUserRole(me.role ?? null);
    } catch {
      setTenantId(null);
      setUserRole(null);
    }
  }, [fetchJson]);

  const loadConversations = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchJson<{ data: SentinelConversation[] }>('/agent/conversations');
      const list = response.data ?? [];
      setConversations(list);
      if (!activeConversationId && list.length) {
        setActiveConversationId(list[0].id);
      }
    } finally {
      setLoading(false);
    }
  }, [activeConversationId, fetchJson]);

  const loadMessages = useCallback(
    async (conversationId: string) => {
      setLoading(true);
      try {
        const response = await fetchJson<{
          data: SentinelMessage[];
          proposals: SentinelActionProposal[];
        }>(`/agent/messages?conversation_id=${encodeURIComponent(conversationId)}`);
        setMessages(response.data ?? []);
        setProposals(response.proposals ?? []);
      } finally {
        setLoading(false);
      }
    },
    [fetchJson],
  );

  useEffect(() => {
    if (!isOpen || !sentinelEnabled) return;
    void loadCurrentUser();
    void loadConversations();
  }, [isOpen, loadConversations, loadCurrentUser, sentinelEnabled]);

  useEffect(() => {
    if (!activeConversationId || !isOpen || !sentinelEnabled) return;
    void loadMessages(activeConversationId);
  }, [activeConversationId, isOpen, loadMessages, sentinelEnabled]);

  useEffect(() => {
    if (!sentinelEnabled) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setIsOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [sentinelEnabled]);

  useEffect(() => {
    if (!sentinelEnabled && isOpen) {
      setIsOpen(false);
    }
  }, [isOpen, sentinelEnabled]);

  const createConversation = useCallback(async () => {
    const scope = computeScope(currentContextHint.selected_entities, currentContextHint.module);
    const scopeId = computeScopeId(currentContextHint.selected_entities, scope);
    const created = await fetchJson<{ id: string }>('/agent/conversations', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        scope_type: scope,
        scope_id: scopeId,
      }),
    });
    await loadConversations();
    setActiveConversationId(created.id);
    setMessages([]);
    setProposals([]);
    return created.id;
  }, [currentContextHint.module, currentContextHint.selected_entities, fetchJson, loadConversations]);

  const sendMessage = useCallback(
    async (value: string) => {
      const trimmed = value.trim();
      if (!trimmed || sending) return;
      setSending(true);
      const userTempId = `u-${Date.now()}`;
      const assistantTempId = `a-${Date.now()}`;
      const now = new Date().toISOString();
      setMessages((prev) => [
        ...prev,
        { id: userTempId, role: 'user', content: trimmed, created_at: now },
        { id: assistantTempId, role: 'assistant', content: '', created_at: now },
      ]);

      let conversationId = activeConversationId;
      if (!conversationId) {
        conversationId = await createConversation();
      }

      try {
        const safeClientContextHints = {
          route: currentContextHint.route,
          module: currentContextHint.module,
          selected_entities: currentContextHint.selected_entities,
        };
        const response = await fetchApi('/agent/chat', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/x-ndjson',
          },
          body: JSON.stringify({
            conversation_id: conversationId,
            message: trimmed,
            client_context_hints: safeClientContextHints,
          }),
        });

        if (!response.ok || !response.body) {
          throw new Error(`Sentinel chat failed (${response.status})`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let runningAssistant = '';

        for (;;) {
          const { value: chunkValue, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(chunkValue, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';
          for (const line of lines) {
            const trimmedLine = line.trim();
            if (!trimmedLine) continue;
            const event = JSON.parse(trimmedLine) as any;
            if (event.type === 'meta') {
              if (event.context_envelope) {
                setCurrentEnvelope(event.context_envelope);
              }
              if (event.conversation_id) {
                setActiveConversationId(event.conversation_id);
              }
            }
            if (event.type === 'delta' && typeof event.content === 'string') {
              runningAssistant += event.content;
              setMessages((prev) =>
                prev.map((msg) => (msg.id === assistantTempId ? { ...msg, content: runningAssistant } : msg)),
              );
            }
            if (event.type === 'proposal' && event.proposal) {
              setProposals((prev) => {
                const exists = prev.some((item) => item.id === event.proposal.id);
                if (exists) return prev;
                return [...prev, event.proposal];
              });
            }
          }
        }
        await loadConversations();
      } catch (err: any) {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === assistantTempId
              ? {
                  ...msg,
                  role: 'system',
                  content: err?.message || 'Sentinel request failed.',
                }
              : msg,
          ),
        );
      } finally {
        setSending(false);
      }
    },
    [activeConversationId, createConversation, currentContextHint, fetchApi, loadConversations, sending],
  );

  const confirmProposal = useCallback(
    async (proposalId: string) => {
      await fetchJson('/agent/actions/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ proposal_id: proposalId }),
      });
      if (activeConversationId) {
        await loadMessages(activeConversationId);
      }
    },
    [activeConversationId, fetchJson, loadMessages],
  );

  const cancelProposal = useCallback(
    async (proposalId: string) => {
      await fetchJson('/agent/actions/cancel', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ proposal_id: proposalId }),
      });
      if (activeConversationId) {
        await loadMessages(activeConversationId);
      }
    },
    [activeConversationId, fetchJson, loadMessages],
  );

  const openSentinel = useCallback(() => {
    if (!sentinelEnabled) return;
    setIsOpen(true);
  }, [sentinelEnabled]);
  const closeSentinel = useCallback(() => setIsOpen(false), []);
  const toggleSentinel = useCallback(() => {
    if (!sentinelEnabled) return;
    setIsOpen((prev) => !prev);
  }, [sentinelEnabled]);

  const value = useMemo(
    () => ({
      isOpen,
      openSentinel,
      closeSentinel,
      toggleSentinel,
    }),
    [closeSentinel, isOpen, openSentinel, toggleSentinel],
  );

  return (
    <SentinelContext.Provider value={value}>
      {children}
      {sentinelEnabled && !isOpen ? (
        <button
          className="fixed bottom-6 right-6 z-[110] h-12 w-12 rounded-full bg-ink text-white shadow-xl inline-flex items-center justify-center"
          onClick={openSentinel}
          aria-label="Open Sentinel"
        >
          <MessageCircle size={18} />
        </button>
      ) : null}
      {sentinelEnabled ? (
        <SentinelDrawer
          open={isOpen}
          onClose={closeSentinel}
          conversations={conversations}
          activeConversationId={activeConversationId}
          onSelectConversation={setActiveConversationId}
          onNewChat={async () => {
            await createConversation();
          }}
          messages={messages}
          proposals={proposals}
          onConfirmProposal={confirmProposal}
          onCancelProposal={cancelProposal}
          onSend={sendMessage}
          sending={sending}
          loading={loading}
          currentContextHint={currentContextHint}
          currentEnvelope={currentEnvelope}
        />
      ) : null}
    </SentinelContext.Provider>
  );
}

export function useSentinel() {
  const context = useContext(SentinelContext);
  if (!context) {
    throw new Error('useSentinel must be used within SentinelProvider');
  }
  return context;
}
